import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Monitor, Tablet, Smartphone, Save, Rocket, Undo2, RotateCcw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlockList } from "@/components/editor/BlockList";
import { BlockProps } from "@/components/editor/BlockProps";
import { AddBlockMenu } from "@/components/editor/AddBlockMenu";
import { DEFAULT_LAYOUT, newBlock } from "@/components/home/pageBlocks";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const DEVICES = [["desktop", Monitor, "100%"], ["tablet", Tablet, "820px"], ["mobile", Smartphone, "400px"]];
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function PageEditorPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const frame = useRef(null);
  const [blocks, setBlocks] = useState(null);
  const [saved, setSaved] = useState(null);
  const [published, setPublished] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [busy, setBusy] = useState(false);
  const [away, setAway] = useState(false);
  const lastChange = useRef({ t: 0, id: null });

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    axios.get(`${API}/admin/page-layout/home`, auth).then((r) => {
      const pub = Array.isArray(r.data.published) ? r.data.published : DEFAULT_LAYOUT;
      const draft = Array.isArray(r.data.draft) ? r.data.draft : pub;
      setPublished(pub); setSaved(draft); setBlocks(draft);
    }).catch((e) => {
      if (e.response?.status === 401) { localStorage.removeItem("adminToken"); navigate("/admin"); }
      else toast.error("Layout konnte nicht geladen werden");
    });
  }, []);

  const post = useCallback((msg) => frame.current?.contentWindow?.postMessage(msg, window.location.origin), []);

  useEffect(() => { if (blocks) post({ type: "at-editor-layout", blocks }); }, [blocks, post]);
  useEffect(() => { post({ type: "at-editor-selected", id: selectedId }); }, [selectedId, post]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || !e.data) return;
      if (e.data.type === "at-editor-ready") { post({ type: "at-editor-layout", blocks }); post({ type: "at-editor-selected", id: selectedId }); }
      if (e.data.type === "at-editor-select") setSelectedId(e.data.id);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [blocks, selectedId, post]);

  const change = (next, typingId = null) => {
    const now = Date.now();
    const merge = typingId && lastChange.current.id === typingId && now - lastChange.current.t < 1000;
    if (!merge) setHistory((h) => [...h.slice(-49), blocks]);
    lastChange.current = { t: now, id: typingId };
    setBlocks(next);
  };
  const onFrameLoad = () => {
    try {
      const loc = frame.current.contentWindow.location;
      setAway(!(loc.pathname === "/" && loc.search.includes("preview=1")));
    } catch {
      setAway(true);
    }
  };
  useEffect(() => {
    const iv = setInterval(() => frame.current && onFrameLoad(), 1000);
    return () => clearInterval(iv);
  });
  const backToPreview = () => { frame.current.src = "/?preview=1"; setAway(false); };
  const undo = () => { if (!history.length) return; setBlocks(history[history.length - 1]); setHistory((h) => h.slice(0, -1)); };
  const selectFromList = (id) => { setSelectedId(id); post({ type: "at-editor-selected", id, scroll: true }); };
  const add = (type) => {
    const b = newBlock(type);
    const idx = blocks.findIndex((x) => x.id === selectedId);
    const next = [...blocks];
    next.splice(idx >= 0 ? idx + 1 : next.length, 0, b);
    change(next);
    setTimeout(() => selectFromList(b.id), 150);
  };

  const request = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };
  const saveDraft = () => request(async () => { await axios.put(`${API}/admin/page-layout/home/draft`, { blocks }, auth); setSaved(blocks); }, "Entwurf gespeichert");
  const publish = () => request(async () => {
    await axios.post(`${API}/admin/page-layout/home/publish`, { blocks }, auth);
    setSaved(blocks); setPublished(blocks);
  }, "Veröffentlicht - die Startseite ist jetzt aktualisiert");
  const discard = () => { if (window.confirm("Alle Änderungen seit der letzten Veröffentlichung verwerfen?")) change(published); };
  const resetDefault = () => {
    if (window.confirm("Startseite auf das Standard-Layout zurücksetzen? (Wird erst mit „Veröffentlichen“ live)")) change(DEFAULT_LAYOUT);
  };

  if (!blocks) return <div className="min-h-screen flex items-center justify-center text-[#888B8D]">Editor wird geladen ...</div>;
  const selected = blocks.find((b) => b.id === selectedId);
  const dirty = !same(blocks, saved);
  const unpublished = !same(blocks, published);

  return (
    <div className="h-screen flex flex-col bg-[#E9E9E7]" data-testid="page-editor">
      <header className="h-14 bg-black text-white flex items-center gap-3 px-4 flex-shrink-0">
        <Link to="/admin/dashboard" className="flex items-center gap-2 text-sm text-[#D9D9D6] hover:text-white" data-testid="editor-back-link">
          <ArrowLeft className="w-4 h-4" /> Admin
        </Link>
        <div className="w-px h-6 bg-[#333]" />
        <div className="font-bold text-sm tracking-wide text-white">SEITEN-EDITOR <span className="text-[#EFDF00]">· Startseite</span></div>
        <span className={`text-xs px-2 py-0.5 ${unpublished ? "bg-[#EFDF00] text-black" : "bg-[#222] text-[#888B8D]"}`} data-testid="editor-status">
          {unpublished ? (dirty ? "Ungespeicherte Änderungen" : "Entwurf - nicht veröffentlicht") : "Live-Stand"}
        </span>
        <div className="flex-1" />
        <div className="flex bg-[#1a1a1a]" data-testid="editor-device-switch">
          {DEVICES.map(([d, Icon]) => (
            <button key={d} type="button" onClick={() => setDevice(d)} aria-label={d}
              className={`p-2 ${device === d ? "bg-[#EFDF00] text-black" : "text-[#888B8D] hover:text-white"}`} data-testid={`editor-device-${d}`}>
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
        <button type="button" onClick={undo} disabled={!history.length} className="p-2 text-[#D9D9D6] hover:text-white disabled:opacity-30" aria-label="Rückgängig" data-testid="editor-undo-btn">
          <Undo2 className="w-4 h-4" />
        </button>
        <a href="/" target="_blank" rel="noreferrer" className="p-2 text-[#D9D9D6] hover:text-white" aria-label="Live-Seite öffnen" data-testid="editor-open-live">
          <ExternalLink className="w-4 h-4" />
        </a>
        <Button onClick={saveDraft} disabled={busy || !dirty} variant="outline" className="h-9 bg-transparent border-[#555] text-white hover:bg-[#222] hover:text-white" data-testid="editor-save-draft-btn">
          <Save className="w-4 h-4 mr-2" /> Entwurf speichern
        </Button>
        <Button onClick={publish} disabled={busy || !unpublished} className="h-9 bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold" data-testid="editor-publish-btn">
          <Rocket className="w-4 h-4 mr-2" /> Veröffentlichen
        </Button>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="w-[380px] bg-white border-r border-[#D9D9D6] flex flex-col min-h-0 flex-shrink-0">
          <div className="p-4 border-b border-[#E5E5E5] overflow-y-auto max-h-[48%]">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#888B8D]">Bereiche · ziehen zum Verschieben</p>
            </div>
            <BlockList blocks={blocks} selectedId={selectedId} onSelect={selectFromList} onChange={change} />
            <div className="mt-3"><AddBlockMenu blocks={blocks} onAdd={add} /></div>
            <div className="flex gap-3 mt-3 text-xs">
              <button type="button" onClick={discard} disabled={!unpublished} className="text-[#888B8D] hover:text-black disabled:opacity-30" data-testid="editor-discard-btn">Änderungen verwerfen</button>
              <button type="button" onClick={resetDefault} className="text-[#888B8D] hover:text-black flex items-center gap-1" data-testid="editor-reset-default-btn">
                <RotateCcw className="w-3 h-3" /> Standard-Layout
              </button>
            </div>
          </div>
          <div className="p-4 overflow-y-auto flex-1">
            <BlockProps block={selected} onChange={(b) => change(blocks.map((x) => (x.id === b.id ? b : x)), b.id)} />
          </div>
        </aside>

        <main className="flex-1 min-w-0 overflow-auto p-4 flex flex-col items-center gap-3">
          {away && (
            <div className="w-full max-w-3xl bg-black text-white text-sm px-4 py-2.5 flex items-center justify-between" data-testid="editor-preview-away">
              <span>Sie sehen gerade eine andere Seite - Änderungen erscheinen nur in der Startseiten-Vorschau.</span>
              <button type="button" onClick={backToPreview} className="bg-[#EFDF00] text-black font-bold px-3 py-1 text-xs" data-testid="editor-back-to-preview-btn">
                Zurück zur Vorschau
              </button>
            </div>
          )}
          <div className="bg-white shadow-xl flex-1 min-h-0 transition-[width] duration-300" style={{ width: DEVICES.find((d) => d[0] === device)[2] }}>
            <iframe ref={frame} src="/?preview=1" title="Vorschau" onLoad={onFrameLoad} className="w-full h-full border-0" data-testid="editor-preview-frame" />
          </div>
        </main>
      </div>
    </div>
  );
}
